import React from 'react';
import {
  NudgeChartSeries,
  NudgeRateBar,
  NudgeRateCaption,
} from '../NudgeStack/types';
import styles from './charts.module.css';

const VIEWBOX_WIDTH = 300;
const VIEWBOX_HEIGHT = 160;
const VERTICAL_PADDING = 6;

const toPolylinePoints = (
  points: number[],
  min: number,
  max: number
): string => {
  if (points.length < 2) return '';
  const span = max - min || 1;
  const usableHeight = VIEWBOX_HEIGHT - VERTICAL_PADDING * 2;

  return points
    .map((point, index) => {
      const x = (index / (points.length - 1)) * VIEWBOX_WIDTH;
      const y =
        VIEWBOX_HEIGHT -
        VERTICAL_PADDING -
        ((point - min) / span) * usableHeight;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');
};

type GrowthComparisonChartProps = {
  series: NudgeChartSeries[];
};

export const GrowthComparisonChart: React.FC<GrowthComparisonChartProps> = ({
  series,
}) => {
  const allPoints = series.flatMap((line) => line.points);
  if (allPoints.length === 0) return null;

  const min = Math.min(...allPoints);
  const max = Math.max(...allPoints);

  return (
    <div className={styles.chartBlock}>
      <svg
        className={styles.growthChart}
        viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
        preserveAspectRatio='none'
        aria-hidden='true'
      >
        {series.map((line) => (
          <polyline
            key={line.name}
            points={toPolylinePoints(line.points, min, max)}
            fill='none'
            stroke={line.color}
            strokeWidth={2.5}
            strokeLinecap='round'
            strokeLinejoin='round'
            vectorEffect='non-scaling-stroke'
          />
        ))}
      </svg>

      <div className={styles.legend}>
        {series.map((line) => (
          <div className={styles.legendItem} key={line.name}>
            <span
              className={styles.legendDot}
              style={{ background: line.color }}
            />
            <span className={styles.legendLabel}>{line.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

type RateComparisonChartProps = {
  bars: NudgeRateBar[];
  captions?: NudgeRateCaption[];
  footnote?: string | null;
};

export const RateComparisonChart: React.FC<RateComparisonChartProps> = ({
  bars,
  captions,
  footnote,
}) => {
  if (bars.length === 0) return null;

  return (
    <div className={styles.chartBlock}>
      {footnote && <p className={styles.footnote}>{footnote}</p>}

      <div className={styles.bars}>
        {bars.map((bar, index) => (
          <div className={styles.barColumn} key={`${bar.value}-${index}`}>
            <span
              className={`${styles.barValue} ${
                bar.highlight ? styles.barValueHighlight : ''
              }`}
            >
              {bar.value}
            </span>
            <div
              className={`${styles.bar} ${bar.highlight ? styles.barHighlight : ''}`}
              style={{ height: `${Math.max(0, Math.min(100, bar.height))}%` }}
            >
              {bar.iconUrl && (
                <img className={styles.barIcon} src={bar.iconUrl} alt='' />
              )}
            </div>
          </div>
        ))}
      </div>

      {captions && captions.length > 0 && (
        <div className={styles.captions}>
          {captions.map((caption) => (
            <div
              className={styles.caption}
              key={caption.text}
              style={{ flex: caption.span }}
            >
              {caption.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
